import { BetaSchemaForm, ProFormColumnsType } from '@ant-design/pro-components';
import { Card, Form } from 'antd';
import dayjs from 'dayjs';
import { createContext, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { RelationshipToCompany, RelationshipToPrimaryApplicant } from '../../../../api/btlApplicationForm/types';
import { doesAddressHistoryCoverMinimumYears } from 'common/addressHistory';
import { gbPhoneNumberAntdRule } from 'utils/gbPhoneNumber';
import { DeleteIcon } from '../../../../components/common/Icons';
import { TITLE_OPTIONS } from '../../../ApplicationFormPage/FullName/types';
import { Address } from '../../common/Address';
import { BaseFormSchemaProps } from '../../types';
import countries from '../../common/Address/countriesList';
import {
  MaritalStatus,
  nationalitiesWithBritainAtStartAndOnlyOnce,
  ResidencyStatus,
  TaxpayerType,
  doesResidencyStatusRequireVisaEndDate,
} from './types';
import { isRetiringWithinTenYears } from 'common/helpers';
import { householdMemberFormSchema } from '../../TopSlicing/Household/householdMemberFormSchema';

type IndividualFormSchemaProps = {
  hasCompanyApplicant: boolean;
  showGrossIncome: boolean;
  isFirstIndividual: boolean;
  variant?: 'btl' | 'oo';
} & BaseFormSchemaProps;

function isOoVariant(props: IndividualFormSchemaProps): boolean {
  return props.variant === 'oo';
}

function AddressHistoryHeading({ props }: { props: IndividualFormSchemaProps }) {
  const form = Form.useFormInstance();

  return (
    <Form.Item noStyle shouldUpdate>
      {() => {
        const hasAddressHistoryError = form.getFieldError(['addressList']).length > 0;

        return (
          <div className={'-mb-2'}>
            <h3 className={'-mb-1'}>
              {props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addressDetails.label' })}
            </h3>
            <p className={`m-0 text-sm ${hasAddressHistoryError ? 'text-red-500' : 'text-gray-500'}`}>
              {props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addressHistory.minimumYears.hint' })}
            </p>
          </div>
        );
      }}
    </Form.Item>
  );
}

const AddressListItemIndexContext = createContext<number | null>(null);

function AddressListItemFields({ schemaProps }: { schemaProps: IndividualFormSchemaProps }) {
  const listIndex = useContext(AddressListItemIndexContext);
  const isCurrentAddress = listIndex === 0;

  return (
    <>
      <Address isEditable={schemaProps.isFormEditable} />
      <BetaSchemaForm
        layoutType={'Embed'}
        columns={[
          {
            valueType: 'group',
            columns: [
              {
                title: () =>
                  schemaProps.formatMessage({
                    id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addressMoveInDate.label',
                  }),
                dataIndex: 'moveInDate',
                valueType: 'date',
                colProps: {
                  span: 6,
                },
                fieldProps: {
                  variant: schemaProps.isFormEditable ? 'outlined' : 'filled',
                  format: {
                    format: 'DD/MM/YYYY',
                    type: 'mask',
                  },
                  placeholder: schemaProps.formatMessage({ id: 'common.dateMask' }),
                },
                formItemProps: {
                  rules: [{ required: true }],
                  getValueFromEvent: date => (date ? dayjs(date).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null),
                },
              },
              ...(isCurrentAddress
                ? []
                : [
                    {
                      title: schemaProps.formatMessage({
                        id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addressMoveOutDate.label',
                      }),
                      dataIndex: 'moveOutDate',
                      valueType: 'date',
                      colProps: {
                        span: 6,
                      },
                      fieldProps: {
                        variant: schemaProps.isFormEditable ? 'outlined' : 'filled',
                        format: {
                          format: 'DD/MM/YYYY',
                          type: 'mask',
                        },
                        placeholder: schemaProps.formatMessage({ id: 'common.dateMask' }),
                      },
                      formItemProps: {
                        rules: [{ required: true }],
                        getValueFromEvent: (date: dayjs.Dayjs | null) =>
                          date ? dayjs(date).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null,
                      },
                    },
                  ]),
            ],
          },
        ]}
      />
    </>
  );
}

const applicantPersonalDetails = (props: IndividualFormSchemaProps, hasTitle = true): ProFormColumnsType[] => [
  {
    hideInForm: !hasTitle,
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.title.label' }),
    dataIndex: 'title',
    valueType: 'select',
    valueEnum: Object.fromEntries(TITLE_OPTIONS.map(value => [value, { text: value }])),
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: true }],
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.firstName.label' }),
    dataIndex: 'firstName',
    valueType: 'text',
    fieldProps: {
      allowClear: false,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: true }],
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.middleName.label' }),
    dataIndex: 'middleName',
    valueType: 'text',
    fieldProps: {
      allowClear: false,
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.lastName.label' }),
    dataIndex: 'lastName',
    valueType: 'text',
    fieldProps: {
      allowClear: false,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: true }],
    },
  },
];

const creditHistory = (props: IndividualFormSchemaProps, items: string[]): ProFormColumnsType[] => {
  return items.map(item => {
    return {
      title: <FormattedMessage id={`btlApplicationForm.APPLICANTS_INDIVIDUAL.${item}.label`} />,
      dataIndex: item,
      valueType: 'radio',
      fieldProps: {
        options: [
          {
            label: props.formatMessage({ id: 'common.yes' }),
            value: true,
          },
          {
            label: props.formatMessage({ id: 'common.no' }),
            value: false,
          },
        ],
      },
      formItemProps: {
        className: '[&_.ant-form-item-label]:!pb-0',
        rules: [
          {
            required: true,
            warningOnly: !props.isDipAccepted,
            type: 'boolean',
            message: props.formatMessage({ id: 'common.error.requiredField' }),
          },
        ],
      },
    } as ProFormColumnsType;
  });
};

export const individualFormSchema = (props: IndividualFormSchemaProps): ProFormColumnsType[] => [
  ...applicantPersonalDetails(props),
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.previousName.label' }),
    dataIndex: 'previousNames',
    valueType: 'formList',
    columns: [...applicantPersonalDetails(props, false)],
    fieldProps: {
      copyIconProps: false,
      max: 1,
      creatorButtonProps: props.isFormEditable && {
        position: 'bottom',
        creatorButtonText: props.formatMessage({
          id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addPreviousName.label',
        }),
      },
      itemRender: ({ listDom }: any, { index, operation }: any) => (
        <Card
          title={`Previous Name ${index + 1}`}
          className={'bg-white/60 border-gray-300 border-dashed w-full'}
          extra={
            operation.remove && props.isFormEditable ? (
              <a onClick={() => operation.remove(index)}>
                <DeleteIcon />
              </a>
            ) : null
          }
          style={{ marginBottom: 16 }}
        >
          {listDom}
        </Card>
      ),
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.dateOfBirth.label' }),
    dataIndex: 'dateOfBirth',
    valueType: 'date',
    fieldProps: {
      format: {
        format: 'DD/MM/YYYY',
        type: 'mask',
      },
      placeholder: props.formatMessage({ id: 'common.dateMask' }),
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: true }],
      getValueFromEvent: date => (date ? dayjs(date).format('YYYY-MM-DD') : null),
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.retirementAge.label' }),
    dataIndex: 'retirementAge',
    valueType: 'digit',
    width: 300,
    fieldProps: {
      precision: 0,
      type: 'number',
    },
    formItemProps: {
      hasFeedback: true,
      rules: [
        {
          required: true,
          warningOnly: !props.isDipAccepted,
          min: 0,
          type: 'number',
        },
      ],
    },
  },
  {
    hideInForm: props.isFirstIndividual,
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.relationshipToPrimaryApplicant.label' }),
    dataIndex: 'relationshipToPrimaryApplicant',
    valueType: 'select',
    fieldProps: {
      options: Object.values(RelationshipToPrimaryApplicant).map((type, index) => ({
        label: (
          <FormattedMessage
            id={`btlApplicationForm.APPLICANTS_INDIVIDUAL.relationshipToPrimaryApplicant.${index}.label`}
          />
        ),
        value: type,
      })),
      showSearch: true,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: true }],
    },
  },
  {
    hideInForm: isOoVariant(props) || !props.isDipAccepted || !props.hasCompanyApplicant,
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.relationshipToSpv.label' }),
    dataIndex: 'relationshipToSpv',
    valueType: 'select',
    fieldProps: {
      options: Object.values(RelationshipToCompany).map((type, index) => ({
        label: <FormattedMessage id={`btlApplicationForm.APPLICANTS_INDIVIDUAL.relationshipToSpv.${index}.label`} />,
        value: type,
      })),
      showSearch: true,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: true }],
      getValueProps: value => ({ value: value === 'UNKNOWN' ? undefined : value }),
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.maritalStatus.label' }),
    dataIndex: 'maritalStatus',
    valueType: 'select',
    fieldProps: {
      options: [
        {
          label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.maritalStatus.0.label' }),
          value: MaritalStatus.SINGLE,
        },
        {
          label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.maritalStatus.1.label' }),
          value: MaritalStatus.MARRIED,
        },
        {
          label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.maritalStatus.2.label' }),
          value: MaritalStatus.CIVIL_PARTNERSHIP,
        },
        {
          label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.maritalStatus.3.label' }),
          value: MaritalStatus.SEPARATED,
        },
        {
          label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.maritalStatus.4.label' }),
          value: MaritalStatus.DIVORCED,
        },
        {
          label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.maritalStatus.5.label' }),
          value: MaritalStatus.WIDOWED,
        },
      ],
      showSearch: true,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: true, warningOnly: !props.isDipAccepted }],
      getValueProps: value => ({ value: value === 'UNKNOWN' ? undefined : value }),
    },
  },
  {
    renderFormItem: () => (
      <h3 className={'-mb-4'}>
        {props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.contactDetails.label' })}
      </h3>
    ),
  },
  // To be sent as {contactType: ContactType, contact: string}[]
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.email.label' }),
    dataIndex: 'email',
    valueType: 'text',
    fieldProps: {
      allowClear: false,
    },
    formItemProps: {
      rules: [{ type: 'email' }, { required: props.isDipAccepted || isOoVariant(props) }],
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.phoneNumber.label' }),
    dataIndex: 'phoneNumber',
    valueType: 'text',
    fieldProps: {
      allowClear: false,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [
        gbPhoneNumberAntdRule(props.formatMessage({ id: 'common.error.validPhoneNumber' })),
        { required: props.isDipAccepted || isOoVariant(props) },
      ],
    },
  },
  {
    renderFormItem: () => <AddressHistoryHeading props={props} />,
  },
  {
    dataIndex: ['addressList'],
    valueType: 'formList',
    fieldProps: {
      copyIconProps: false,
      min: 1,
      creatorButtonProps: props.isFormEditable && {
        position: 'bottom',
        creatorButtonText: props.formatMessage({
          id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addAddress.label',
        }),
      },
      itemRender: ({ listDom }: any, { index, operation, fields }: any) => (
        <AddressListItemIndexContext.Provider value={index}>
          <Card
            title={
              index === 0
                ? `${props.formatMessage({
                    id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.currentAddress.label',
                  })}`
                : `${props.formatMessage({
                    id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.previousAddress.label',
                  })} ${index}`
            }
            className={'bg-white/60 border-gray-300 border-dashed'}
            extra={
              // Keep at least one current address card so required fields (and scroll targets) always exist.
              index > 0 && fields?.length > 1 && operation.remove && props.isFormEditable ? (
                <a onClick={() => operation.remove(index)}>
                  <DeleteIcon
                    titleAccess={props.formatMessage({
                      id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.removeAddress.label',
                    })}
                  />
                </a>
              ) : null
            }
            style={{ marginBottom: 16 }}
          >
            {listDom}
          </Card>
        </AddressListItemIndexContext.Provider>
      ),
    },
    formItemProps: {
      validateTrigger: 'onSubmit',
      rules: [
        {
          // ProFormList renders its own Form.ErrorList that `help` can't suppress, so the message is
          // deliberately empty — the heading above the list turns red and carries the wording instead.
          validator: async (_: unknown, value: { moveInDate?: string | Date | null }[] | undefined) => {
            if (!doesAddressHistoryCoverMinimumYears(value ?? [])) {
              return Promise.reject(new Error(''));
            }
          },
        },
      ],
    },
    columns: [
      {
        renderFormItem: () => <AddressListItemFields schemaProps={props} />,
      },
    ],
  },
  {
    renderFormItem: () => (
      <h3 className={'-mb-4'}>
        {props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.residency.label' })}
      </h3>
    ),
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.nationality.label' }),
    dataIndex: 'nationality',
    valueType: 'select',
    fieldProps: {
      options: nationalitiesWithBritainAtStartAndOnlyOnce,
      showSearch: true,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: true, warningOnly: !props.isDipAccepted }],
    },
  },
  {
    dataIndex: 'residencyStatus',
    valueType: 'group',
    columns: [
      {
        hideInForm: isOoVariant(props) || !props.isDipAcceptedOrLater,
        title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.dualNationality.label' }),
        dataIndex: ['residencyStatus', 'dualNationality'],
        valueType: 'select',
        dependencies: ['nationality'],
        fieldProps: form => ({
          options: nationalitiesWithBritainAtStartAndOnlyOnce.filter(
            item => item.value !== form?.getFieldValue?.('nationality'),
          ),
          showSearch: true,
        }),
      },
      {
        title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.residencyStatus.label' }),
        dataIndex: ['residencyStatus', 'status'],
        valueType: 'select',
        fieldProps: {
          options: [
            {
              label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.residencyStatus.0.label' }),
              value: ResidencyStatus.BRITISH_CITIZEN,
            },
            {
              label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.residencyStatus.1.label' }),
              value: ResidencyStatus.EU_SETTLED_OR_PRE_SETTLED,
            },
            {
              label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.residencyStatus.2.label' }),
              value: ResidencyStatus.INDEFINITE_LEAVE_TO_REMAIN,
            },
            {
              label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.residencyStatus.3.label' }),
              value: ResidencyStatus.SPOUSAL_VISA,
            },
            {
              label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.residencyStatus.4.label' }),
              value: ResidencyStatus.OTHER,
            },
          ],
        },
        formItemProps: {
          hasFeedback: true,
          rules: [{ required: true, warningOnly: !props.isDipAccepted }],
          getValueProps: value => ({ value: value === 'UNKNOWN' ? undefined : value }),
        },
      },
    ],
  },
  {
    valueType: 'dependency',
    name: ['residencyStatus'],
    columns: ({ residencyStatus }) => [
      {
        hideInForm: !doesResidencyStatusRequireVisaEndDate(residencyStatus?.status),
        title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.visaEndDate.label' }),
        dataIndex: ['residencyStatus', 'visaEndDate'],
        valueType: 'date',
        fieldProps: {
          format: {
            format: 'DD/MM/YYYY',
            type: 'mask',
          },
          placeholder: props.formatMessage({ id: 'common.dateMask' }),
        },
        formItemProps: {
          getValueFromEvent: date => (date ? dayjs(date).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null),
        },
      },
    ],
  },
  {
    hideInForm: isOoVariant(props),
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.isExpatriate.label' }),
    dataIndex: 'isExpatriate',
    valueType: 'radio',
    fieldProps: {
      options: [
        {
          label: props.formatMessage({ id: 'common.yes' }),
          value: true,
        },
        {
          label: props.formatMessage({ id: 'common.no' }),
          value: false,
        },
      ],
    },
    formItemProps: {
      rules: [{ required: true, warningOnly: !props.isDipAccepted, type: 'boolean' }],
    },
  },
  {
    hideInForm: isOoVariant(props),
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.countryOfResidence.label' }),
    dataIndex: 'countryOfResidence',
    valueType: 'select',
    fieldProps: {
      options: countries,
      showSearch: true,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: props.isDipAccepted }],
    },
  },
  {
    renderFormItem: () => (
      <h3 className={'-mb-4'}>
        {props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.experience.header' })}
      </h3>
    ),
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.isFirstTimeBuyer.label' }),
    dataIndex: 'isFirstTimeBuyer',
    valueType: 'radio',
    fieldProps: {
      options: [
        {
          label: props.formatMessage({ id: 'common.yes' }),
          value: true,
        },
        {
          label: props.formatMessage({ id: 'common.no' }),
          value: false,
        },
      ],
    },
    formItemProps: {
      rules: [{ required: true, warningOnly: !props.isDipAccepted, type: 'boolean' }],
    },
  },
  {
    hideInForm: isOoVariant(props),
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.isFirstTimeLandlord.label' }),
    dataIndex: 'isFirstTimeLandlord',
    valueType: 'radio',
    fieldProps: {
      options: [
        {
          label: props.formatMessage({ id: 'common.yes' }),
          value: true,
        },
        {
          label: props.formatMessage({ id: 'common.no' }),
          value: false,
        },
      ],
    },
    formItemProps: {
      rules: [{ required: props.isDipAccepted, type: 'boolean' }],
    },
  },
  {
    hideInForm: isOoVariant(props),
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.isPortfolioLandlord.label' }),
    dataIndex: 'isPortfolioLandlord',
    valueType: 'radio',
    fieldProps: {
      options: [
        {
          label: props.formatMessage({ id: 'common.yes' }),
          value: true,
        },
        {
          label: props.formatMessage({ id: 'common.no' }),
          value: false,
        },
      ],
    },
    formItemProps: {
      rules: [{ required: props.isDipAccepted, type: 'boolean' }],
    },
  },
  {
    hideInForm: isOoVariant(props) || !props.isDipAcceptedOrLater,
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.yearsLandlordExperience.label' }),
    dataIndex: 'yearsLandlordExperience',
    valueType: 'digit',
    width: 300,
    fieldProps: {
      precision: 0,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [
        {
          required: true,
          message: props.formatMessage({
            id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.yearsLandlordExperience.error',
          }),
          min: 0,
          type: 'number',
        },
      ],
    },
    transform: value => ({ termInMonths: value * 12 }),
  },
  {
    hideInForm: isOoVariant(props),
    renderFormItem: () => (
      <h3 className={'-mb-4'}>
        {props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.finances.label' })}
      </h3>
    ),
  },
  {
    hideInForm: isOoVariant(props),
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.taxpayerType.label' }),
    dataIndex: 'taxpayerType',
    valueType: 'select',
    fieldProps: {
      options: [
        {
          label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.taxpayerType.0.label' }),
          value: TaxpayerType.UK_BASIC_RATE,
        },
        {
          label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.taxpayerType.1.label' }),
          value: TaxpayerType.COMPANY,
        },
        {
          label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.taxpayerType.2.label' }),
          value: TaxpayerType.UK_HIGHER_RATE,
        },
        {
          label: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.taxpayerType.3.label' }),
          value: TaxpayerType.NON_UK_TAXPAYER,
        },
      ],
      showSearch: true,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: true }],
      getValueProps: value => ({ value: value === 'UNKNOWN' ? undefined : value }),
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.grossIncome.label' }),
    hideInForm: isOoVariant(props) || !props.showGrossIncome,
    dataIndex: 'grossIncome',
    valueType: { type: 'money', locale: 'en-GB' },
    width: 300,
    fieldProps: {
      precision: 0,
      placeholder: '£',
    },
    formItemProps: {
      hasFeedback: true,
      rules: [
        {
          required: props.isDipAccepted,
          type: 'number',
        },
        {
          min: 0,
          message: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.grossIncome.error' }),
          type: 'number',
        },
      ],
    },
  },
  {
    hideInForm: isOoVariant(props),
    valueType: 'dependency',
    name: ['retirementAge', 'dateOfBirth'],
    columns: ({ retirementAge, dateOfBirth }) => [
      {
        title: props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.projectedRetirementIncome.label' }),
        hideInForm: !isRetiringWithinTenYears(dateOfBirth, retirementAge),
        dataIndex: 'projectedRetirementPayeIncome',
        valueType: { type: 'money', locale: 'en-GB' },
        width: 300,
        fieldProps: {
          precision: 0,
          placeholder: '£',
        },
        formItemProps: {
          hasFeedback: true,
          rules: [
            {
              required: props.isDipAccepted && isRetiringWithinTenYears(dateOfBirth, retirementAge),
              type: 'number',
            },
            {
              min: 0,
              message: props.formatMessage({
                id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.projectedRetirementIncome.error',
              }),
              type: 'number',
            },
          ],
        },
      },
    ],
  },
  ...(isOoVariant(props)
    ? householdMemberFormSchema({
        formatMessage: props.formatMessage,
        isFormEditable: props.isFormEditable,
        isDipAccepted: props.isDipAccepted,
        isDipAcceptedOrLater: props.isDipAcceptedOrLater,
        isEmploymentIncomeRequired: true,
        variant: 'oo',
      })
    : []),
  {
    renderFormItem: () => (
      <h3 className={'-mb-4'}>
        {props.formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.creditHistory.header' })}
      </h3>
    ),
  },
  {
    valueType: 'group',
    columns: [
      ...creditHistory(props, [
        'bankruptcy',
        'ccj',
        'creditDefaults',
        'paydayLoans',
        'propertyRepossession',
        'criminalConvictions',
        'missedFinancePayments',
      ]),
    ],
  },
];
